import React, { useState } from 'react';
import { Pressable, ScrollView, StyleSheet, View } from 'react-native';
import PageHeader from '../../components/account/PageHeader';
import EmptyState from '../../components/ui/EmptyState';
import Icon from '../../components/ui/Icon';
import Text from '../../components/ui/Text';
import {
  AppNotification,
  notificationIcon,
  NotificationKind,
} from '../../data/notifications';
import {
  colors,
  hairline,
  radii,
  screenPadding,
  spacing,
  useResponsive,
} from '../../theme';

/**
 * The notification feed, as a page rather than a sheet.
 *
 * The sheet is still there and still right for the bell - a glance from
 * wherever you are. This is the same feed reached from the account, where it is
 * a destination: it gets filters, grouping by day, and room for the list to be
 * long without fighting a drag-to-dismiss gesture.
 *
 * Grouping is read off the `when` string rather than computed. Nothing in the
 * app holds a timestamp - a real feed would send both the label and the group -
 * so the buckets below match the strings the seed data actually uses, and
 * anything unrecognised falls into "Earlier" rather than vanishing.
 */

type Filter = 'all' | 'bookings' | 'offers' | 'updates';

const FILTERS: Array<{ key: Filter; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'bookings', label: 'Bookings' },
  { key: 'offers', label: 'Offers' },
  { key: 'updates', label: 'Updates' },
];

/** Which kinds each chip shows. `all` is everything, so it is not listed. */
const KINDS: Record<Exclude<Filter, 'all'>, NotificationKind[]> = {
  bookings: ['booking'],
  offers: ['offer'],
  updates: ['reminder', 'account'],
};

/** Today, yesterday, or everything older. */
const bucketOf = (when: string): string => {
  const lower = when.toLowerCase();
  if (lower.includes('now') || lower.endsWith('ago')) {
    return 'Today';
  }
  if (lower.includes('yesterday')) {
    return 'Yesterday';
  }
  return 'Earlier';
};

const ORDER = ['Today', 'Yesterday', 'Earlier'];

type Props = {
  notifications: AppNotification[];
  onBack: () => void;
  onRead: (id: string) => void;
  onReadAll: () => void;
};

function NotificationsScreen({
  notifications,
  onBack,
  onRead,
  onReadAll,
}: Props) {
  const resp = useResponsive();
  const [filter, setFilter] = useState<Filter>('all');

  const shown =
    filter === 'all'
      ? notifications
      : notifications.filter(item => KINDS[filter].includes(item.kind));

  /* Grouped in the feed's own order, not sorted - the list already arrives
     newest first, and re-sorting on a formatted string would reorder it by
     alphabet. */
  const groups = ORDER.map(label => ({
    label,
    items: shown.filter(item => bucketOf(item.when) === label),
  })).filter(group => group.items.length > 0);

  const unread = notifications.filter(item => !item.read).length;

  return (
    <View style={styles.root}>
      <PageHeader
        title="Notifications"
        onBack={onBack}
        // Hidden when there is nothing to mark: a control that cannot change
        // anything still reads as one that failed.
        action={unread > 0 ? 'Mark all read' : undefined}
        onAction={onReadAll}
      />

      {/*
        `flexGrow: 0` on the rail, and the chips centred inside it.

        A horizontal ScrollView still stretches its content on the cross axis,
        and with no height of its own it claims whatever vertical space is
        going - so each chip grew to fill the screen the moment the row was
        given room, and the labels clipped on the way there. The rail hugs its
        chips; `alignItems` stops them stretching to its height.
      */}
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        style={styles.chipRail}
        /* Outside the page's scroller, so it centres on the column itself. */
        contentContainerStyle={[styles.chips, resp.page]}
      >
        {FILTERS.map(item => {
          const on = item.key === filter;
          return (
            <Pressable
              key={item.key}
              accessibilityRole="button"
              accessibilityState={{ selected: on }}
              accessibilityLabel={item.label}
              onPress={() => setFilter(item.key)}
              style={[styles.chip, on ? styles.chipOn : null]}
            >
              <Text
                variant="labelSm"
                color={on ? colors.onPrimary : colors.inkBody}
              >
                {item.label}
              </Text>
            </Pressable>
          );
        })}
      </ScrollView>

      {/*
        `flex: 1` on the scroller itself, not only on its content.

        Inside a flex column a ScrollView with no flex of its own sizes to its
        content and overflows the parent, so the foot of a long list is simply
        cut off - worst on whichever filter shows the most. Flexed, it takes the
        space left under the header and scrolls inside it.
      */}
      <ScrollView
        style={styles.scroller}
        /* Zero on a phone; on a tablet it holds the page to a readable
           column and centres it. See `theme/responsive.ts`. */
        /* `resp.page`, not `resp.column`: `page` above already carries the
           gutter, and `column` is the centring inset alone - zero on a phone -
           so appending it wiped the gutter. */
        contentContainerStyle={[styles.page, resp.page]}
        showsVerticalScrollIndicator={false}
      >
        {groups.length === 0 ? (
          /* The shared component, not the hand-built copy that used to sit
             here - `EmptyState` exists precisely to stop those diverging, and
             this is the screen's ordinary state now rather than a rare one. */
          <EmptyState
            icon="bell"
            title="Nothing here"
            message={
              filter === 'all'
                ? 'Updates about your bookings will show up here.'
                : 'No notifications of that kind yet.'
            }
          />
        ) : (
          groups.map(group => (
            <View key={group.label} style={styles.group}>
              <Text variant="labelSm" color={colors.inkMuted}>
                {group.label}
              </Text>

              {group.items.map(item => (
                <Pressable
                  key={item.id}
                  accessibilityRole="button"
                  accessibilityLabel={`${item.title}. ${item.body}. ${item.when}${
                    item.read ? '' : '. Unread'
                  }`}
                  onPress={() => onRead(item.id)}
                  style={({ pressed }) => [
                    styles.row,
                    // Unread gets the tinted ground. Read rows keep the plain
                    // one, so the difference is visible without a legend.
                    item.read ? null : styles.rowUnread,
                    pressed ? styles.pressed : null,
                  ]}
                >
                  <View style={styles.mark}>
                    <Icon
                      name={notificationIcon[item.kind]}
                      size={17}
                      color={colors.primary}
                    />
                  </View>

                  <View style={styles.body}>
                    <Text variant="subtitle" numberOfLines={2}>
                      {item.title}
                    </Text>
                    <Text variant="caption" color={colors.inkMuted}>
                      {item.body}
                    </Text>
                    <Text variant="caption" color={colors.inkSubtle}>
                      {item.when}
                    </Text>
                  </View>

                  {item.read ? (
                    <Icon
                      name="chevronRight"
                      size={15}
                      color={colors.borderStrong}
                    />
                  ) : (
                    <View style={styles.dot} />
                  )}
                </Pressable>
              ))}
            </View>
          ))
        )}
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: colors.background,
  },
  chipRail: {
    flexGrow: 0,
  },
  chips: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingHorizontal: screenPadding,
    paddingVertical: spacing.md,
  },
  chip: {
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.sm,
    borderRadius: radii.pill,
    backgroundColor: colors.surface,
    ...hairline,
  },
  chipOn: {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
  },

  scroller: {
    flex: 1,
  },
  page: {
    paddingHorizontal: screenPadding,
    // Clears the floating tab bar, which overlays the bottom of the screen.
    paddingBottom: 120,
  },
  group: {
    gap: spacing.sm,
    paddingBottom: spacing.lg,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing.md,
    padding: spacing.md,
    borderRadius: radii.lg,
    backgroundColor: colors.surface,
    ...hairline,
  },
  rowUnread: {
    backgroundColor: colors.surfaceAccent,
    borderColor: colors.surfaceAccent,
  },
  mark: {
    width: 34,
    height: 34,
    borderRadius: radii.sm,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.surface,
  },
  body: {
    flex: 1,
    gap: 2,
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    marginTop: 6,
    backgroundColor: colors.success,
  },

  empty: {
    alignItems: 'center',
    gap: spacing.sm,
    paddingVertical: spacing.xxxl,
    paddingHorizontal: spacing.xl,
  },
  pressed: {
    opacity: 0.6,
  },
});

export default NotificationsScreen;
